import { PageModal, useModal } from "@becon-team/becon-fe-lib/my-modal";
import { cn } from "@becon-team/becon-fe-lib/my-tailwind-css";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { GroupBtnFormInput } from "components/(form)/GroupBtnFormInput";
import { InquiryFormLayout } from "components/(form)/InquiryFormLayout";
import { modalIds } from "libs/my-modal";

import { useFormPutSkinInquiry } from "hooks/@skin/useFormPutSkinInquiry";

import type { Undefinable } from "@becon-team/becon-fe-lib/my-types";

type ModalParams = {
  userId: string;
  onSuccess?: () => void;
  allowUnchangedSubmit?: boolean;
};

export const SkinInquiryModal = () => {
  const modalId = modalIds["skin-inquiry"];
  const { t } = useTranslation();

  const { modal, closeModal } = useModal(modalId);
  const { isOpened, params } = modal;
  const { userId, onSuccess, allowUnchangedSubmit = false } = (params as Undefinable<ModalParams>) || {};

  const {
    tightAfterWashFaceOptions,
    isOilyAndIsDryOptions,
    issuesOptions,
    productCountOptions,
    usingOptions,
    yesOrNoOptions,
    sleepTimeOptions,
    stressOptions,
    tightAfterWashFaceRegister,
    isOilyRegister,
    isDryRegister,
    issuesRegister,
    productCountRegister,
    useBodyMoistureProductRegister,
    sleepTimeRegister,
    stressLevelRegister,
    dietRegister,
    smokingRegister,
    drinkingRegister,
    sunscreenRegister,
    skinCarePeriodicallyRegister,
    errorMessages,
    onSubmit,
    isSubmitting,
    isFormChanged,
  } = useFormPutSkinInquiry({
    userId,
    onSuccess: () => {
      closeModal({
        onFinish: () => {
          onSuccess && onSuccess();
        },
      });
    },
  });
  return (
    <PageModal
      closeModal={closeModal}
      titleSection={t("문진 작성")}
      btnSection={
        <button
          form={modalId}
          className={cn("my-btn-primary w-full", { "my-loading": isSubmitting })}
          type="submit"
          disabled={!isFormChanged && !allowUnchangedSubmit}
        >
          {t("제출하기")}
        </button>
      }
      isOpened={isOpened}
    >
      <form id={modalId} onSubmit={onSubmit}>
        <div className="space-y-sp-40 p-inner-padding pt-sp-16">
          <InquiryFormLayout
            number="01"
            label={t("세안 후 피부가 당기나요?")}
            errorMessage={errorMessages.tightAfterWashFace}
            isRequired={true}
          >
            <GroupBtnFormInput
              className="grid-cols-2 "
              options={tightAfterWashFaceOptions}
              registerProps={tightAfterWashFaceRegister}
            />
          </InquiryFormLayout>

          <InquiryFormLayout
            number="02"
            label={t("피부가 번들거리게 느껴지나요?")}
            errorMessage={errorMessages.isOily}
            isRequired={true}
          >
            <GroupBtnFormInput className="grid-cols-2" options={isOilyAndIsDryOptions} registerProps={isOilyRegister} />
          </InquiryFormLayout>

          <InquiryFormLayout
            number="03"
            label={t("피부가 건조하게 느껴지나요?")}
            errorMessage={errorMessages.isDry}
            isRequired={true}
          >
            <GroupBtnFormInput className="grid-cols-2" options={isOilyAndIsDryOptions} registerProps={isDryRegister} />
          </InquiryFormLayout>

          <InquiryFormLayout
            number="04"
            label={t("피부 고민을 선택해 주세요")}
            info={t("최대 {{n}}개까지 선택할 수 있어요", { n: 5 })}
            errorMessage={errorMessages.issues}
            isRequired={true}
          >
            <GroupBtnFormInput className="grid-cols-2 " options={issuesOptions} registerProps={issuesRegister} />
          </InquiryFormLayout>
        </div>

        <div className="border-t-4 border-base-600 bg-base-50 p-inner-padding pb-sp-16">
          <div className="text-lg font-light text-base-600">*{t("여기서부터는 필수가 아닌 추가 문진이에요")}</div>
          <div className="mt-sp-24 space-y-sp-40 ">
            <InquiryFormLayout
              number="05"
              label={t("세안 후 사용하는 스킨 케어 제품 개수는?")}
              errorMessage={errorMessages.productCount}
            >
              <GroupBtnFormInput
                className="grid-cols-2 "
                options={productCountOptions}
                registerProps={productCountRegister}
              />
            </InquiryFormLayout>

            <InquiryFormLayout
              number="06"
              label={t("샤워 후 바디 보습 제품을 사용하나요?")}
              errorMessage={errorMessages.useBodyMoistureProduct}
            >
              <GroupBtnFormInput
                className="grid-cols-2"
                options={usingOptions}
                registerProps={useBodyMoistureProductRegister}
              />
            </InquiryFormLayout>

            <InquiryFormLayout
              number="07"
              label={t("하루에 얼마나 주무시나요?")}
              errorMessage={errorMessages.sleepTime}
            >
              <GroupBtnFormInput className="grid-cols-2" options={sleepTimeOptions} registerProps={sleepTimeRegister} />
            </InquiryFormLayout>

            <InquiryFormLayout
              number="8"
              label={t("스트레스 정도는 어떠신가요?")}
              errorMessage={errorMessages.stressLevel}
            >
              <GroupBtnFormInput className="grid-cols-2" options={stressOptions} registerProps={stressLevelRegister} />
            </InquiryFormLayout>

            <InquiryFormLayout number="9" label={t("다이어트를 하고 계신가요?")} errorMessage={errorMessages.diet}>
              <GroupBtnFormInput className="grid-cols-2" options={yesOrNoOptions} registerProps={dietRegister} />
            </InquiryFormLayout>

            <InquiryFormLayout number="10" label={t("흡연을 하시나요?")} errorMessage={errorMessages.smoking}>
              <GroupBtnFormInput className="grid-cols-2" options={yesOrNoOptions} registerProps={smokingRegister} />
            </InquiryFormLayout>

            <InquiryFormLayout number="11" label={t("음주를 하시나요?")} errorMessage={errorMessages.drinking}>
              <GroupBtnFormInput className="grid-cols-2" options={yesOrNoOptions} registerProps={drinkingRegister} />
            </InquiryFormLayout>

            <InquiryFormLayout
              number="12"
              label={t("UV 차단제(선크림)를 매일 사용하시나요")}
              errorMessage={errorMessages.sunscreen}
            >
              <GroupBtnFormInput className="grid-cols-2" options={yesOrNoOptions} registerProps={sunscreenRegister} />
            </InquiryFormLayout>

            <InquiryFormLayout
              number="13"
              label={t("피부 관리를 주기적으로 받으시나요?")}
              errorMessage={errorMessages.skinCarePeriodically}
            >
              <GroupBtnFormInput
                className="grid-cols-2"
                options={yesOrNoOptions}
                registerProps={skinCarePeriodicallyRegister}
              />
            </InquiryFormLayout>
          </div>
        </div>
      </form>
    </PageModal>
  );
};

export const useSkinInquiryModal = () => {
  const { modal, openModal, closeModal } = useModal(modalIds["skin-inquiry"]);

  const openModalCallback = useCallback(
    (params: ModalParams) => {
      openModal(params);
    },
    [openModal],
  );

  return {
    openModal: openModalCallback,
    closeModal,
    isOpened: modal.isOpened,
  };
};
